import * as React from "react";

import "./index.scss";

import {cs}             from "@Common/react/chain-services";
import {fragments}      from "@Common/react/fragments";
import {keyed}          from "@Common/react/keyed";
import {GlobalEvent}    from "@Common/react/global-event";
import {Interval}       from "@Common/react/interval";
import {keyCombo}       from "@Common/react/key-combo";
import {Rerender}       from "@Common/react/rerender";
import {UseState}       from "@Common/react/use-state";
import {GlobalKeyDown2} from "@Common/ui-components/browser/global-key-down-2";
import {OnGlobalKeyDownEscape} from "@Common/ui-components/browser/global-key-down-escape";
import {isChildNode}    from "@Common/utils/browser/is-child-node";
import {sort}           from "@Common/utils/collections";
import {chain}          from "@Common/utils/fs";
import {equalDeep}      from "@Common/utils/objects";

import {bootstrap}     from "./bootstrap";
import {Errors}        from "./errors/errors";
import {getTargetElem} from "./get-target-elem";
import {Rects}         from "./rects/rects";
import {TrackMousePos} from "./track-mouse-pos";
import {Inspect} from "./inspect/inspect";
import {getSmallestElemAt} from "./get-smallest-elem-at";
import {consumeContext, provideContext1} from "@Common/react/context";
import {QuickClientApiProxy} from "./common/quick-http-api";
import {getWebstormUrl} from "./common/get-webstorm-url";
import {getTagPath} from "./get-tag-path";
import {getELoc} from "../../../ui-components/browser/get-e-loc";

console.log("QA Tags Viewer started. To reveal, press Ctrl+Alt+Q");
cs(
    ({}, next) => bootstrap({next}),
    ["indexScript", ({}, next) => next(document.currentScript ?? Array.from(document.scripts).find(script => script.src.includes('/xadmin/index.js')))],
    ({indexScript}, next) => provideContext1("apis2", QuickClientApiProxy({host: indexScript?.src.replace(/\/index\.js\b.*?$/, "")}), next),
    ["show", (_, next) => UseState({next})],

    ({show}, next) => fragments(
        next(),
        GlobalKeyDown2({
            check: keyCombo("ctrl+alt+q"),
            action: (e) => {
                e.preventDefault();
                show.change(v=>!v);
            },
        }),
        GlobalKeyDown2({
            check: keyCombo("ctrl+alt+œ"),
            action: (e) => {
                e.preventDefault();
                show.change(v=>!v);
            },
        }),
    ),

    ({show}, next) => show.value && next(),
    ["rerenderAll", (_, next) => Rerender({next})],
    ["qaTags", ({}, next) => next((() => {
        return Array.from(
            document.getElementById("root").querySelectorAll("[xd-qa-type]")
        );
    })())],
    // ({qaTags}, next) => next(console.log("qaTags", qaTags)),

    ["rerender", (_, next) => Rerender({next})],
    ["tags2", ({qaTags}, next) => {
        const visibleElem = getTargetElem();
        return next(chain(
            qaTags,
            (_) => qaTags.map((tag) => ({
                tag,
                rect: tag.getBoundingClientRect(),
                hidden: visibleElem && !isChildNode(tag, visibleElem),
                xdQa: {
                    type: tag.getAttribute("xd-qa-type"),
                    name: tag.getAttribute("xd-qa-name"),
                },
            })),
            (_) => sort(_, ({rect}) => rect.width * rect.height),
        ));
    }],


    consumeContext("apis2"),
    ["highlight", (_, next) => UseState({next})],
    ["inspect", (_, next) => UseState({next})],
    ({tags2, apis2, highlight, rerender, inspect, rerenderAll}) => (
        <div
            className="overlay-43i"
            onClick={(e) => {
                if (e.metaKey && e.shiftKey) {
                    e.stopPropagation();
                    e.preventDefault();
                    const smallestElem = getSmallestElemAt(getELoc(e), getTargetElem());
                    (async () => {
                        const code = await apis2.getCodeForTag(getTagPath(smallestElem));
                        if (code) {
                            // await apis.openFile(last(code));
                            window.open(getWebstormUrl(last(code)))
                        }
                    })();
                }
            }}
            // onKeyDown={(e) => {
            //     console.log(e.clientX);
            // }}
        >
            {GlobalEvent({
                eventName: "scroll",
                action: () => rerender.invoke(),
                // action: debounce(() => {
                //     rerender.invoke();
                // }, 100),
            })}
            {Interval({
                action: () => rerenderAll.invoke(),
                delay: 1000,
            })}
            {inspect.value && cs(
                keyed(inspect.value.key),
                () => fragments(
                    Inspect({
                        pos: inspect.value.pos,
                        tag: inspect.value.tag,
                    }),
                    OnGlobalKeyDownEscape({
                        action: () => inspect.onChange(null),
                    }),
                ),
            )}
            {TrackMousePos({
                key: "i",
                action: (pos) => {
                    const smallestElem = getSmallestElemAt(pos, getTargetElem());
                    return inspect.onChange({
                        key: Date.now(),
                        tag: smallestElem,
                        pos,
                    });
                },
            })}

            {Rects({
                tags2,
                ...highlight.value && {
                    isHighlighted: (xdQa) => highlight.value.hasOwnProperty("name") ? (
                        equalDeep(xdQa, highlight.value)
                    ) : (
                        xdQa.type === highlight.value.type
                    ),
                },
            })}

            {Errors({
                tags2,
                onHover: (xdQa) => highlight.onChange(xdQa),
                onOut: () => highlight.onChange(null),
            })}
        </div>
    ),
);
